import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mail, Send, Users, User, Globe, CheckCheck, Reply, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function MessagesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [users, setUsers] = useState([]);
  const [inbox, setInbox] = useState(null);
  const [sent, setSent] = useState([]);
  const [tab, setTab] = useState("inbox");

  const [mode, setMode] = useState("single"); // single | multiple | all (admin)
  const [recipient, setRecipient] = useState("");
  const [selected, setSelected] = useState({});
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const composeRef = useRef(null);
  const bodyRef = useRef(null);

  const startReply = (m) => {
    setMode("single");
    setRecipient(m.sender_id);
    composeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => bodyRef.current?.focus(), 400);
    openMessage(m);
  };

  const load = useCallback(async () => {
    try {
      const [{ data: msgs }, { data: us }] = await Promise.all([
        api.get("/messages"),
        api.get("/messages/users"),
      ]);
      setInbox(msgs.inbox || []);
      setSent(msgs.sent || []);
      setUsers(us.users || []);
    } catch (_e) {
      void _e;
      setInbox([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openMessage = async (m) => {
    if (m.read) return;
    setInbox((list) => list.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    try { await api.post(`/messages/${m.id}/read`); } catch (_e) { void _e; }
  };

  const deleteMessage = async (m, from) => {
    if (!window.confirm(t("messages.delete_confirm"))) return;
    if (from === "inbox") setInbox((list) => list.filter((x) => x.id !== m.id));
    else setSent((list) => list.filter((x) => x.id !== m.id));
    try {
      await api.delete(`/messages/${m.id}`);
      toast.success(t("messages.deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      await load();
    }
  };

  const send = async () => {
    const text = body.trim();
    if (!text) return toast.error(t("messages.body_required"));
    let payload;
    if (isAdmin && mode === "all") {
      payload = { to_all: true, recipient_ids: [], body: text };
    } else if (isAdmin && mode === "multiple") {
      const ids = Object.keys(selected).filter((k) => selected[k]);
      if (ids.length === 0) return toast.error(t("messages.pick_recipient"));
      payload = { recipient_ids: ids, body: text };
    } else {
      if (!recipient) return toast.error(t("messages.pick_recipient"));
      payload = { recipient_ids: [recipient], body: text };
    }
    setSending(true);
    try {
      const { data } = await api.post("/messages", payload);
      toast.success(t("messages.sent_ok").replace("{n}", data.sent_to));
      setBody("");
      setSelected({});
      await load();
      setTab("sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const unread = (inbox || []).filter((m) => !m.read).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="messages-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
          <Mail className="w-7 h-7 text-olive" />
          {t("messages.title")}
        </h1>
        <p className="text-sm text-espresso/70 mt-1">{t("messages.sub")}</p>
      </div>

      {/* Compose */}
      <div className="bg-white rounded-xl border border-app-border p-5 space-y-3" data-testid="compose-box">
        <h2 className="text-xs font-bold uppercase tracking-widest text-olive flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> {t("messages.compose")}
        </h2>

        {isAdmin && (
          <div className="flex gap-2 flex-wrap" data-testid="admin-send-modes">
            {[
              { key: "single", icon: User, label: t("messages.mode_single") },
              { key: "multiple", icon: Users, label: t("messages.mode_multiple") },
              { key: "all", icon: Globe, label: t("messages.mode_all") },
            ].map(({ key, icon: MIcon, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  mode === key
                    ? "bg-olive text-white border-olive"
                    : "bg-white text-espresso/70 border-app-border hover:border-olive/50"
                }`}
                data-testid={`mode-${key}`}
              >
                <MIcon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        )}

        {(!isAdmin || mode === "single") && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-olive font-bold block mb-1.5">
              {t("messages.recipient")}
            </label>
            <select
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="w-full sm:w-72 text-sm bg-white border border-app-border rounded-lg px-3 py-2 outline-none focus:border-olive"
              data-testid="recipient-select"
            >
              <option value="">{t("messages.pick_recipient")}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}

        {isAdmin && mode === "multiple" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-olive font-bold block mb-1.5">
              {t("messages.recipients")} ({Object.values(selected).filter(Boolean).length})
            </label>
            <div className="max-h-48 overflow-y-auto border border-app-border rounded-lg p-2 grid sm:grid-cols-2 gap-1" data-testid="recipients-multi-list">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm text-espresso px-2 py-1 rounded hover:bg-cream-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected[u.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [u.id]: e.target.checked }))}
                    className="accent-[#6b7245]"
                    data-testid={`recipient-check-${u.id}`}
                  />
                  {u.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {isAdmin && mode === "all" && (
          <div className="text-sm text-espresso/70 bg-olive/5 border border-olive/20 rounded-lg px-3 py-2" data-testid="all-users-note">
            {t("messages.all_note").replace("{n}", users.length)}
          </div>
        )}

        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("messages.body_placeholder")}
          rows={4}
          ref={bodyRef}
          data-testid="message-body"
        />
        <Button
          onClick={send}
          disabled={sending}
          className="bg-olive hover:bg-olive/90"
          data-testid="send-message-button"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <><Send className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" /> {t("messages.send")}</>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <div>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setTab("inbox")}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              tab === "inbox" ? "bg-olive text-white border-olive" : "bg-white text-espresso/70 border-app-border"
            }`}
            data-testid="tab-inbox"
          >
            {t("messages.inbox")} {unread > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1.5">{unread}</span>}
          </button>
          <button
            type="button"
            onClick={() => setTab("sent")}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              tab === "sent" ? "bg-olive text-white border-olive" : "bg-white text-espresso/70 border-app-border"
            }`}
            data-testid="tab-sent"
          >
            {t("messages.sent_tab")}
          </button>
        </div>

        {inbox === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
        ) : tab === "inbox" ? (
          inbox.length === 0 ? (
            <div className="text-sm text-espresso/50" data-testid="inbox-empty">{t("messages.none_inbox")}</div>
          ) : (
            <div className="space-y-2" data-testid="inbox-list">
              {inbox.map((m, i) => (
                <div
                  key={m.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openMessage(m)}
                  className={`w-full text-left rtl:text-right bg-white rounded-xl border p-4 transition-colors cursor-pointer ${
                    m.read ? "border-app-border" : "border-olive/50 bg-olive/5"
                  }`}
                  data-testid={`inbox-message-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm text-espresso ${m.read ? "font-medium" : "font-bold"}`}>{m.sender_name}</span>
                    {!m.read && <span className="w-2 h-2 rounded-full bg-red-500" data-testid={`unread-dot-${i}`} />}
                    {m.to_all && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-olive/10 text-olive">
                        {t("messages.broadcast_badge")}
                      </span>
                    )}
                    <span className="ml-auto rtl:ml-0 rtl:mr-auto text-[11px] text-espresso/40">{timeLabel(m.created_at)}</span>
                  </div>
                  <p className="text-sm text-espresso/80 mt-1.5 whitespace-pre-wrap">{m.body}</p>
                  <div className="mt-2 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); startReply(m); }}
                      className="flex items-center gap-1 text-xs font-semibold text-olive hover:underline"
                      data-testid={`reply-message-button-${i}`}
                    >
                      <Reply className="w-3.5 h-3.5 rtl:-scale-x-100" /> {t("messages.reply")}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteMessage(m, "inbox"); }}
                      className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline"
                      data-testid={`delete-inbox-message-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> {t("messages.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : sent.length === 0 ? (
          <div className="text-sm text-espresso/50" data-testid="sent-empty">{t("messages.none_sent")}</div>
        ) : (
          <div className="space-y-2" data-testid="sent-list">
            {sent.map((m, i) => (
              <div key={m.id} className="bg-white rounded-xl border border-app-border p-4" data-testid={`sent-message-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-espresso">
                    {t("messages.to")}: {m.to_all ? t("messages.mode_all") : m.recipient_name}
                  </span>
                  {m.read && !m.to_all && (
                    <span className="flex items-center gap-0.5 text-[10px] text-olive font-semibold">
                      <CheckCheck className="w-3.5 h-3.5" /> {t("messages.read_badge")}
                    </span>
                  )}
                  <span className="ml-auto rtl:ml-0 rtl:mr-auto text-[11px] text-espresso/40">{timeLabel(m.created_at)}</span>
                </div>
                <p className="text-sm text-espresso/80 mt-1.5 whitespace-pre-wrap">{m.body}</p>
                <button
                  type="button"
                  onClick={() => deleteMessage(m, "sent")}
                  className="mt-2 flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline"
                  data-testid={`delete-sent-message-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> {t("messages.delete")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
